<template>
  <!-- Mobile Notifications with sliding effect -->
  <Transition name="notifications-slide">
    <div v-if="openNotice" class="mobile-notifications-overlay" @click="onClose">
      <Transition name="notifications-slide-panel">
        <div v-if="openNotice" class="mobile-notifications-panel" @click.stop>
          <div class="notifications-header">
            <div class="notifications-header__content">
              <h2 class="notifications-title">{{ trans('Notifications') }}</h2>
              
              <button class="notifications-close" @click="onClose">
                <svg class="icon ic-close" width="24" height="24">
                  <use xlink:href="@/assets/sprites/sprite.svg#ic-close"></use>
                </svg>
              </button>
            </div>
            
            <div class="notifications-actions">
              <button class="mark-all-read-btn" @click="handleMarkAllAsRead">
                <svg class="icon ic-check" width="17" height="9">
                  <use xlink:href="@/assets/sprites/sprite.svg#ic-check"></use>
                </svg>
                {{ trans('Mark all as read') }}
              </button>
            </div>
          </div>

          <div class="notifications-content">
            <div class="tabs-page tabs-page_line">
              <TabHeader v-model="activeTab" :tabs="tabs" />

              <TabContent v-if="activeTab === 'all'" :is-active="true" />
              <TabContent
                v-else-if="activeTab === 'unread'"
                :read-filter="false"
                :is-active="true"
                @refresh-count="fetchNotificationsCount"
              />
              <TabContent
                v-else-if="activeTab === 'read'"
                :read-filter="true"
                :is-active="true"
                @refresh-count="fetchNotificationsCount"
              />
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { trans } from '@/common/language/translations';
import { computed, onMounted, ref } from 'vue';
import TabHeader from '@/components/Notifications/TabHeader.vue';
import TabContent from '@/components/Notifications/TabContent.vue';
import axios from '@/common/lib/axios';
import { useNotificationStore } from '@/store/notification';
import { User_URL } from '@/common/lib/url';

export interface NotificationTabHeader {
  id: string;
  label: string;
  count: number;
  countClass?: string;
}

interface NotificationsCountResponse {
  success: boolean;
  result: {
    read: number;
    unread: number;
  };
}

defineProps({
  openNotice: {
    type: Boolean,
    default: false,
    required: true
  }
});

const emit = defineEmits(['close', 'allNotificationsRead']);

const notificationStore = useNotificationStore();
const activeTab = ref('all');

const readCount = computed(() => notificationStore.getReadCount);
const unreadCount = computed(() => notificationStore.getUnreadCount);
const allCount = computed(() => notificationStore.getAllCount);

const tabs = computed<NotificationTabHeader[]>(() => [
  {
    id: 'all',
    label: trans('All'),
    count: allCount.value,
    countClass: 'tabs-page__count_red'
  },
  {
    id: 'unread',
    label: trans('Unread'),
    count: unreadCount.value
  },
  {
    id: 'read',
    label: trans('Read'),
    count: readCount.value
  }
]);

const fetchNotificationsCount = async () => {
  try {
    const response = await axios.get<NotificationsCountResponse>(User_URL + 'notifications/count');
    if (response.data.success) {
      const notificationStore = useNotificationStore();
      notificationStore.setReadCount(response.data.result.read);
      notificationStore.setUnreadCount(response.data.result.unread);
    }
  } catch (error) {
    console.error('An error occurred while fetching notifications count:', error);
  }
};

const setupNotificationFetching = () => {
  fetchNotificationsCount();
  setInterval(fetchNotificationsCount, 600000); // Fetch every 10 minutes (600000ms)
};

onMounted(() => {
  setupNotificationFetching();
});

const handleMarkAllAsRead = async () => {
  await notificationStore.markAllAsRead();
};

const onClose = () => {
  activeTab.value = 'all';
  emit('close');
};
</script>

<style scoped lang="scss">
@use '@/assets/styles/tabs-page.scss';

// Mobile Notifications Overlay
.mobile-notifications-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
}

// Mobile Notifications Panel (centered)
.mobile-notifications-panel {
  width: 100%;
  max-width: 400px;
  max-height: 80vh;
  background: white;
  border-radius: 1.6rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

// Header Section
.notifications-header {
  background: white;
  flex-shrink: 0;
  padding: 2rem 2rem 1.5rem 2rem;
  
  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }
}

.notifications-title {
  font-size: 2.6rem;
  font-weight: 600;
  color: #0A1629;
  margin: 0;
}

.notifications-close {
  background: none;
  border: none;
  padding: 0.8rem;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
  
  .icon {
    fill: #0A1629;
    height: 2rem;
  }
}

.notifications-actions {
  display: flex;
  justify-content: flex-end;
}

.mark-all-read-btn {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem 1.2rem;
  background: #ffffff;
  color: #737494;
  border: 1px solid #F5F5F9;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 1.4rem;
  font-weight: 500;
  
  .icon {
    fill: #737494;
    width: 17px;
    height: 9px;
  }
}

// Content Section
.notifications-content {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

// Fade and Scale Animations
.notifications-slide-enter-active,
.notifications-slide-leave-active {
  transition: opacity 0.3s ease;
}

.notifications-slide-enter-from,
.notifications-slide-leave-to {
  opacity: 0;
}

.notifications-slide-panel-enter-active,
.notifications-slide-panel-leave-active {
  transition: all 0.3s ease;
}

.notifications-slide-panel-enter-from,
.notifications-slide-panel-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

// RTL Support (centered panel works the same for both LTR and RTL)
// No specific RTL overrides needed as the centered panel works for both directions
</style>
